/**
 * daily_shipments — 일별 출하 집계 (RQC raw daily_shipment과 별개).
 *
 * 컬렉션 구조: `daily_shipments/{YYYY-MM-DD}`
 *   - date: 일자 (문자열 YYYY-MM-DD 또는 Timestamp)
 *   - gov_m3 / cust_m3: 관급/사급 **실제 출하량** (RQC shipped 합)
 *   - gov_planned_m3 / cust_planned_m3: 관급/사급 **예정량** (RQC qty 합)
 *     · 잔량은 planned - m3 로 사용 측에서 계산 (저장하지 않음)
 *     · 옛 데이터(2026-05 이전 backfill)는 gov_m3=예정량으로 들어있을 수 있음
 *   - source: "RQC_AUTO" | "manual" 등
 *
 * 옛 필드명 `gong_m3` (관급) / `sa_m3` (사급) 잔재 데이터 fallback.
 */

export interface DailyShipment {
  /** 문서 ID = "YYYY-MM-DD" */
  id: string;
  date: string;
  /** 관급 실제 출하량 (㎥) */
  gov_m3: number;
  /** 사급 실제 출하량 (㎥) */
  cust_m3: number;
  /** 실제 출하 총합 = gov_m3 + cust_m3 */
  total_m3: number;
  /** 관급 예정량 (㎥) — 신규 필드, 옛 데이터엔 없을 수 있음 */
  gov_planned_m3?: number;
  /** 사급 예정량 (㎥) — 신규 필드, 옛 데이터엔 없을 수 있음 */
  cust_planned_m3?: number;
  /** 예정 총합 = gov_planned_m3 + cust_planned_m3 (둘 다 undefined면 undefined) */
  total_planned_m3?: number;
  /**
   * 그날 회전수(탕수) = 유효 송장 건수. 출하실적 화면용으로 songjang 에서 채운다
   * ('삭제' 상태 제외). 물량이 아니라 대수 지표라 gov/cust 구분이 없다.
   */
  trips?: number;
  source?: string;
}

function _num(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  const n = parseFloat(String(v ?? ""));
  return Number.isFinite(n) ? n : 0;
}

function _str(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  // Timestamp 객체
  if (typeof v === "object" && "toDate" in (v as object)) {
    const d = (v as { toDate: () => Date }).toDate();
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, "0");
    const day = String(d.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return String(v);
}

export function coerceDailyShipment(
  id: string,
  data: Record<string, unknown>,
): DailyShipment {
  // 옛 필드명 fallback: gov_m3 ?? gong_m3, cust_m3 ?? sa_m3
  const gov = _num(data.gov_m3 ?? data.gong_m3);
  const cust = _num(data.cust_m3 ?? data.sa_m3);

  const hasGovPlanned  = data.gov_planned_m3  != null;
  const hasCustPlanned = data.cust_planned_m3 != null;
  const govPlanned  = hasGovPlanned  ? _num(data.gov_planned_m3)  : undefined;
  const custPlanned = hasCustPlanned ? _num(data.cust_planned_m3) : undefined;
  const totalPlanned =
    govPlanned == null && custPlanned == null
      ? undefined
      : (govPlanned ?? 0) + (custPlanned ?? 0);

  return {
    id,
    date: _str(data.date) || id,
    gov_m3: gov,
    cust_m3: cust,
    total_m3: gov + cust,
    gov_planned_m3:  govPlanned,
    cust_planned_m3: custPlanned,
    total_planned_m3: totalPlanned,
    trips: data.trips != null ? _num(data.trips) : undefined,
    source: typeof data.source === "string" ? data.source : undefined,
  };
}
